import React, { useState, useRef, useEffect } from 'react';

// ============================================================================
// EmailStatusMarker
// ============================================================================
// A small colored dot shown beside timeline date cells to indicate the
// email-workflow status for that date field.
//
// Statuses:
//   not_applicable  → light grey dot (no workflow for this date)
//   pending         → gold dot (workflow exists, email not yet sent)
//   draft_opened    → teal dot (email draft was opened in mail app)
//   sent            → green dot (user confirmed email was sent)
//
// Clicking the dot opens a small popover to manually change status.
// ============================================================================

const EMAIL_STATUSES = [
  { value: 'not_applicable', label: 'Not applicable', color: '#b0b0b0', border: '#b0b0b0', tooltip: 'No email workflow' },
  { value: 'pending',        label: 'Pending',        color: 'var(--brio-gold, #9F902A)',        border: 'var(--brio-gold, #9F902A)',   tooltip: 'Email pending' },
  { value: 'draft_opened',   label: 'Draft opened',   color: 'var(--brio-teal, #689581)',        border: 'var(--brio-teal, #689581)',   tooltip: 'Draft opened' },
  { value: 'sent',           label: 'Sent',           color: 'var(--brio-orange, #E07A5F)',      border: 'var(--brio-orange, #E07A5F)', tooltip: 'Email sent' },
];

const STATUS_MAP = Object.fromEntries(EMAIL_STATUSES.map(s => [s.value, s]));

export function getEmailStatusDef(status) {
  return STATUS_MAP[status] || STATUS_MAP.not_applicable;
}

export default function EmailStatusMarker({ status, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const popRef = useRef(null);

  const current = getEmailStatusDef(status);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const isGrey = !status || status === 'not_applicable';

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: '3px', flexShrink: 0 }}>
      {/* The dot */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen(prev => !prev);
        }}
        title={current.tooltip}
        disabled={disabled}
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: current.color,
          border: `1.5px solid ${current.border}`,
          cursor: disabled ? 'default' : 'pointer',
          padding: 0,
          opacity: isGrey ? 0.3 : 1,
          transition: 'opacity 0.15s',
          flexShrink: 0,
        }}
      />
      {/* Popover */}
      {open && (
        <div
          ref={popRef}
          style={{
            position: 'absolute',
            top: '14px',
            left: '-4px',
            zIndex: 100,
            background: 'var(--brio-white, #fff)',
            border: '1px solid var(--brio-border)',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            padding: '4px 0',
            minWidth: '130px',
            fontFamily: 'var(--brio-font-family)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ padding: '3px 8px', fontSize: '8px', color: 'var(--brio-grey)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
            Email status
          </div>
          {EMAIL_STATUSES.map(s => (
            <button
              key={s.value}
              onClick={() => {
                if (onChange) onChange(s.value);
                setOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                width: '100%',
                padding: '4px 8px',
                background: status === s.value ? 'var(--brio-grey-light, #f1f5f9)' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '10px',
                fontFamily: 'var(--brio-font-family)',
                textAlign: 'left',
                color: 'inherit',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--brio-grey-light, #f1f5f9)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = status === s.value ? 'var(--brio-grey-light, #f1f5f9)' : 'transparent'; }}
            >
              <span style={{
                width: '8px', height: '8px', borderRadius: '50%',
                background: s.color, border: `1.5px solid ${s.border}`,
                flexShrink: 0,
              }} />
              <span>{s.label}</span>
              {status === s.value && <span style={{ marginLeft: 'auto', fontSize: '9px', color: 'var(--brio-grey)' }}>✓</span>}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
